import { formatUsd } from "@/lib/displayFormat";
import type {
  AppetiteCounts,
  IndicationOutcome,
} from "@/lib/submissions/brokerSubmissions";

// How a fan-out leg's outcome reads (BRK-S298, BRK-S305): one wording for the
// comparison's columns and the WC wizard's appetite notices, so a carrier
// reads the same on both.

/** A referral's chip (BRK-S354; Nav, 2026-10-15): what the quote needs, never
 *  that a review is under way, since nothing sends a referral to the carrier's
 *  underwriter yet (PMS-S663). */
export const REFERRED_LABEL = "Needs underwriter review";

/** What the broker does with a referral where they can't ask from the
 *  portal (Nav, 2026-10-15): a role without `submission:edit`, or a quote
 *  result's line. Where they can, the popup offers Request underwriter
 *  review instead (BRK-S463, PMS-S871). */
export const REFERRAL_NEXT = "To have it reviewed, contact Signal.";

/** A referral in plain words (BRK-S354; Nav, 2026-10-14: "Referred can be a
 *  lot of different meanings"): whose underwriter has to review it and what
 *  that means for its price, named by its carrier. One wording wherever a
 *  quote is referred. `premium` is the price it came back with, if any: an
 *  indication, not a quote. */
export function referralLine(carrier: string, premium: string | null): string {
  const review = `${carrier}’s underwriter needs to review this application before ${carrier} can quote.`;
  return premium
    ? `${review} Until then, ${formatUsd(premium)} is its estimated premium.`
    : review;
}

/** A leg's outcome in a word or two. An appetite-only leg (PMS-S681) is a
 *  referral when none of its class codes is in appetite outright. */
export function outcomeLabel(
  outcome: IndicationOutcome,
  appetite?: AppetiteCounts | null,
): string {
  switch (outcome) {
    case "quoted":
      return "Indication ready";
    case "appetite_only":
      return appetite && appetite.inAppetite === 0 && appetite.referralOnly > 0
        ? "Referral"
        : "In appetite";
    case "referred":
      return REFERRED_LABEL;
    case "declined":
      return "Declined";
    case "needs_information":
      return "Needs information";
    case "error":
      return "Unavailable";
  }
}

/** A leg's outcome as a notice: what it means for the broker next. Reasons,
 *  counts and missing fields are shown beside it. A referral names `carrier`,
 *  whose underwriter has to review it (BRK-S354). */
export function outcomeNotice(
  outcome: IndicationOutcome,
  appetite: AppetiteCounts | null | undefined,
  carrier: string,
): string {
  switch (outcome) {
    case "appetite_only":
      return `${outcomeLabel(outcome, appetite)} — priced when it quotes`;
    case "referred":
      return `${referralLine(carrier, null)} ${REFERRAL_NEXT}`;
    case "needs_information":
      return "Needs more information";
    default:
      return outcomeLabel(outcome, appetite);
  }
}

/** An appetite-only leg's class codes, counted by appetite. Empty when it
 *  counted none. */
export function appetiteLine(a: AppetiteCounts): string {
  return [
    a.inAppetite ? `${a.inAppetite} in appetite` : null,
    a.referralOnly ? `${a.referralOnly} referral` : null,
    a.outOfAppetite ? `${a.outOfAppetite} out of appetite` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** How a leg's answer stands, for its dot on the WC wizard's markets line
 *  (BRK-S359): priced, open to a quote, needing attention, or closed. */
export type OutcomeTone = "priced" | "open" | "attention" | "closed";

export function outcomeTone(outcome: IndicationOutcome): OutcomeTone {
  switch (outcome) {
    case "quoted":
      return "priced";
    case "appetite_only":
      return "open";
    case "referred":
    case "needs_information":
      return "attention";
    case "declined":
    case "error":
      return "closed";
  }
}

/** The order a phone's markets line counts the answers in. */
const COUNTED = [
  "indication",
  "in appetite",
  "referral",
  "needs underwriter review",
  "needs information",
  "declined",
  "unavailable",
] as const;

/** Each answer counted, in a fixed order, for a phone's markets line
 *  (BRK-S359): "1 indication · 1 in appetite · 1 declined". */
export function marketCounts(
  legs: readonly {
    outcome: IndicationOutcome;
    appetite?: AppetiteCounts | null;
  }[],
): string {
  const word = (leg: (typeof legs)[number]): (typeof COUNTED)[number] => {
    switch (leg.outcome) {
      case "quoted":
        return "indication";
      case "appetite_only":
        return outcomeLabel(leg.outcome, leg.appetite) === "Referral"
          ? "referral"
          : "in appetite";
      case "referred":
        return "needs underwriter review";
      case "needs_information":
        return "needs information";
      case "declined":
        return "declined";
      case "error":
        return "unavailable";
    }
  };
  const counts = new Map<string, number>();
  for (const leg of legs) {
    const w = word(leg);
    counts.set(w, (counts.get(w) ?? 0) + 1);
  }
  return COUNTED.filter((w) => counts.has(w))
    .map((w) => {
      const n = counts.get(w) ?? 0;
      if (n === 1) return `${n} ${w}`;
      // A noun takes its plural; a "needs" takes the verb's: "2 need …".
      const nouns = w === "indication" || w === "referral";
      return `${n} ${nouns ? `${w}s` : w.replace(/^needs /, "need ")}`;
    })
    .join(" · ");
}
